/**
 * <load-more-products>
 *
 * Replaces numbered pagination on collection pages with a manual
 * "Load more" button (visible from page 1 onward). Once the last page
 * has been loaded, the button is replaced with an "end of collection"
 * message.
 *
 * Usage (added by main-collection-product-grid.liquid):
 * <load-more-products
 *   data-section-id="{{ section.id }}"
 *   data-current-page="{{ paginate.current_page }}"
 *   data-total-pages="{{ paginate.pages }}"
 * >
 *   <div class="load-more-products__spinner" hidden>...</div>
 *   <button class="load-more-products__button">Load more</button>
 *   <p class="load-more-products__end" hidden>End of collection</p>
 * </load-more-products>
 */
class LoadMoreProducts extends HTMLElement {
  constructor() {
    super();

    this.sectionId = this.dataset.sectionId;
    this.currentPage = parseInt(this.dataset.currentPage, 10) || 1;
    this.totalPages = parseInt(this.dataset.totalPages, 10) || 1;
    this.isLoading = false;

    this.spinner = this.querySelector('.load-more-products__spinner');
    this.button = this.querySelector('.load-more-products__button');
    this.endMessage = this.querySelector('.load-more-products__end');

    // The grid this element should append new products into.
    this.grid = document.getElementById('product-grid');

    this._onButtonClick = () => this.loadNextPage();
  }

  connectedCallback() {
    this.button.addEventListener('click', this._onButtonClick);

    if (this.currentPage >= this.totalPages) {
      // Nothing left to paginate — show the end message immediately.
      this.showEndMessage();
    } else {
      this.showButton();
    }
  }

  disconnectedCallback() {
    this.button.removeEventListener('click', this._onButtonClick);
  }

  buildPageUrl(page) {
    // Uses the current URL so any active filters/sort params are preserved.
    const url = new URL(window.location.href);
    url.searchParams.set('page', page);
    url.searchParams.set('section_id', this.sectionId);
    return url.toString();
  }

  async loadNextPage() {
    if (this.isLoading || this.currentPage >= this.totalPages) return;

    this.isLoading = true;
    this.showSpinner();

    const nextPage = this.currentPage + 1;
    const url = this.buildPageUrl(nextPage);

    try {
      const response = await fetch(url);
      if (!response.ok) throw new Error(`Request failed with status ${response.status}`);

      const html = await response.text();
      const doc = new DOMParser().parseFromString(html, 'text/html');
      const newGrid = doc.getElementById('product-grid');

      if (newGrid && this.grid) {
        Array.from(newGrid.children).forEach((item) => {
          this.grid.appendChild(item);
        });
      }

      this.currentPage = nextPage;
      this.isLoading = false;

      if (this.currentPage >= this.totalPages) {
        this.showEndMessage();
      } else {
        this.showButton();
      }
    } catch (error) {
      this.isLoading = false;
      this.showButton();
      console.error('[load-more-products] Failed to load next page:', error);
    }
  }

  showSpinner() {
    this.spinner.hidden = false;
    this.button.hidden = true;
    this.endMessage.hidden = true;
  }

  showButton() {
    this.spinner.hidden = true;
    this.endMessage.hidden = true;
    this.button.hidden = false;
  }

  showEndMessage() {
    this.spinner.hidden = true;
    this.button.hidden = true;
    this.endMessage.hidden = false;
  }
}

if (!customElements.get('load-more-products')) {
  customElements.define('load-more-products', LoadMoreProducts);
}